import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Card, Typography, Space, Tag, Select } from 'antd';
import { IoMic, IoStop } from 'react-icons/io5';
import { useRealtimePCMRecorder } from '@/hooks/useRealtimePCMRecorder';

const { Text, Title } = Typography;

const API_BASE_URL = '';

interface TranscriptItem {
  id: number;
  text: string;
  isFinal: boolean;
  timestamp: number;
}

/**
 * 单个引擎的 SSE 连接 + 转写结果管理
 *
 * ready 事件时序（waitForReady=true 时，如 QwenAsr）：
 * - connected: WebSocket 已连接（但 session 可能还没配置好）
 * - ready: session.created + updateSession 完成，可以安全发送音频
 *
 * waitForReady=false 时（如 SpeechController）：
 * - connected 即表示可以发送音频
 */
function useAsrEngine(apiPath: string, label: string, waitForReady = false) {
  const [items, setItems] = useState<TranscriptItem[]>([]);
  const [partial, setPartial] = useState('');
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sessionIdRef = useRef('');
  const idCounter = useRef(0);
  // 外部可以 await 这个 promise 来等待 ready
  const readyResolveRef = useRef<(() => void) | null>(null);

  const connect = useCallback((extraParams = ''): Promise<void> => {
    const sessionId = `cmp_${label}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionIdRef.current = sessionId;
    setItems([]);
    setPartial('');
    setError(null);
    setConnected(false);
    idCounter.current = 0;

    const readyPromise = new Promise<void>((resolve) => {
      readyResolveRef.current = resolve;
      // 8秒超时保护，防止后端异常导致永远 pending
      setTimeout(() => {
        if (readyResolveRef.current) {
          console.warn(`[${label}] ready timeout after 8s, force resolve`);
          readyResolveRef.current?.();
          readyResolveRef.current = null;
        }
      }, 8000);
    });

    const es = new EventSource(
      `${API_BASE_URL}${apiPath}/start?sessionId=${sessionId}${extraParams}`
    );
    eventSourceRef.current = es;

    es.addEventListener('connected', () => {
      console.log(`[${label}] WebSocket connected`);
      if (!waitForReady) {
        // 不需要等 ready 的引擎（如 SpeechController），connected 即可开始
        setConnected(true);
        readyResolveRef.current?.();
        readyResolveRef.current = null;
      } else {
        console.log(`[${label}] waiting for ready event...`);
      }
    });

    // ready = session 配置完成，可以发送音频（QwenAsr 专用）
    // 重连成功后也会重新触发 ready，清除重连错误提示
    es.addEventListener('ready', () => {
      console.log(`[${label}] ready - session configured`);
      setConnected(true);
      setError(null);
      readyResolveRef.current?.();
      readyResolveRef.current = null;
    });

    es.addEventListener('closed', (e: MessageEvent) => {
      console.log(`[${label}] closed:`, e.data);
      setConnected(false);
      // 如果还没 ready 就关闭了，也要 resolve 防止永远 pending
      readyResolveRef.current?.();
      readyResolveRef.current = null;
    });

    // 后端 WebSocket 断开后自动重连中（Qwen ASR 专用）
    es.addEventListener('reconnecting', (e: MessageEvent) => {
      console.warn(`[${label}] reconnecting:`, e.data);
      setConnected(false);
      setError('WebSocket断开，正在自动重连...');
    });

    // 重连成功后会重新发送 ready 事件，上面的 ready 监听器会自动处理

    es.addEventListener('partial', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        setPartial(data.text || '');
      } catch { /* ignore */ }
    });

    es.addEventListener('final', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (data.text?.trim()) {
          setItems(prev => [...prev, {
            id: ++idCounter.current,
            text: data.text,
            isFinal: true,
            timestamp: data.timestamp || Date.now(),
          }]);
          setPartial('');
        }
      } catch { /* ignore */ }
    });

    es.addEventListener('error', (e: MessageEvent) => {
      if (e.data) {
        try {
          const data = JSON.parse(e.data);
          const errMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data);
          setError(errMsg);
        } catch {
          setError(String(e.data));
        }
      }
    });

    es.onmessage = (e: MessageEvent) => {
      console.log(`[${label}] SSE message:`, e.data);
    };

    es.onerror = (e) => {
      console.error(`[${label}] SSE error:`, { readyState: es.readyState, event: e });
      if (es.readyState === 2) {
        setConnected(false);
        setError(prev => prev || 'SSE连接已关闭');
      } else if (es.readyState === 0) {
        setError(prev => prev || '连接中断，正在重试...');
      }
      // 出错时也 resolve 防止永远 pending
      readyResolveRef.current?.();
      readyResolveRef.current = null;
    };

    return readyPromise;
  }, [apiPath, label]);

  const sendAudio = useCallback((pcmData: ArrayBuffer) => {
    if (!sessionIdRef.current) return;
    fetch(`${API_BASE_URL}${apiPath}/audio?sessionId=${sessionIdRef.current}`, {
      method: 'POST',
      body: new Uint8Array(pcmData),
      headers: { 'Content-Type': 'application/octet-stream' },
    }).catch(() => {});
  }, [apiPath]);

  const stop = useCallback(() => {
    if (sessionIdRef.current) {
      fetch(`${API_BASE_URL}${apiPath}/stop?sessionId=${sessionIdRef.current}`, {
        method: 'POST',
      }).catch(() => {});
    }
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setConnected(false);
    readyResolveRef.current?.();
    readyResolveRef.current = null;
  }, [apiPath]);

  const cleanup = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    readyResolveRef.current?.();
    readyResolveRef.current = null;
  }, []);

  return { items, partial, connected, error, connect, sendAudio, stop, cleanup };
}

/**
 * 转写结果展示面板
 */
const TranscriptPanel: React.FC<{
  title: string;
  tag: string;
  tagColor: string;
  items: TranscriptItem[];
  partial: string;
  connected: boolean;
  error: string | null;
}> = ({ title, tag, tagColor, items, partial, connected, error }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items, partial]);

  return (
    <Card
      title={
        <Space>
          <span>{title}</span>
          <Tag color={tagColor}>{tag}</Tag>
          <Tag color={connected ? 'green' : 'default'}>
            {connected ? '已连接' : '未连接'}
          </Tag>
        </Space>
      }
      style={{ height: '100%' }}
      styles={{ body: { padding: '12px', height: 'calc(100% - 57px)', overflow: 'hidden' } }}
    >
      {error && (
        <div className="mb-2 p-2 bg-red-50 text-red-600 rounded text-sm">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}
      <div
        ref={scrollRef}
        style={{ height: '100%', overflowY: 'auto' }}
        className="space-y-2"
      >
        {items.map(item => (
          <div key={item.id} className="p-2 bg-gray-50 rounded text-sm">
            <Text>{item.text}</Text>
            <div className="text-xs text-gray-400 mt-1">
              #{item.id} · {new Date(item.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}
        {partial && (
          <div className="p-2 bg-blue-50 rounded text-sm italic">
            <Text type="secondary">{partial}</Text>
            <div className="text-xs text-gray-400 mt-1">识别中...</div>
          </div>
        )}
        {items.length === 0 && !partial && (
          <div className="text-center text-gray-400 py-8">
            等待转写结果...
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * ASR 实时转写对比页面
 * 同时启动 SpeechController (paraformer) 和 QwenAsrRealtimeController (qwen3-asr)
 * 共享同一个麦克风音频流，展示原生转写结果（不含 tryMerge）
 */
const AsrCompare: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [language, setLanguage] = useState('zh');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const speechEngine = useAsrEngine('/api/speech', 'speech', false);
  const qwenEngine = useAsrEngine('/api/asr', 'qwen', true);

  const onData = useCallback(
    (pcmData: ArrayBuffer) => {
      speechEngine.sendAudio(pcmData);
      qwenEngine.sendAudio(pcmData);
    },
    [speechEngine.sendAudio, qwenEngine.sendAudio]
  );

  const { startRecording: startRec, stopRecording: stopRec } = useRealtimePCMRecorder({
    onData,
    sampleRate: 16000,
    channelCount: 1,
    dataInterval: 1000,
  });

  const handleStart = useCallback(async () => {
    try {
      // 同时建立两个 SSE 连接，等待两个引擎都 ready
      const speechReady = speechEngine.connect();
      const qwenReady = qwenEngine.connect(`&language=${language}`);

      // 等待两个引擎的 session 都配置完成（ready 事件）
      await Promise.all([speechReady, qwenReady]);

      // 开始录音
      await startRec();
      setIsRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('启动失败:', err);
    }
  }, [speechEngine, qwenEngine, startRec, language]);

  const handleStop = useCallback(() => {
    stopRec();
    speechEngine.stop();
    qwenEngine.stop();
    setIsRecording(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // stopped
  }, [stopRec, speechEngine, qwenEngine]);

  // 组件卸载清理
  useEffect(() => {
    return () => {
      speechEngine.cleanup();
      qwenEngine.cleanup();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={4} className="mb-1">实时 ASR 转写对比</Title>
          <Text type="secondary">
            同一音频流同时发送到两个引擎，展示原生转写结果（无 tryMerge）
          </Text>
        </div>
        <Space>
          <Select
            value={language}
            onChange={setLanguage}
            disabled={isRecording}
            style={{ width: 100 }}
            options={[
              { value: 'zh', label: '中文' },
              { value: 'en', label: 'English' },
              { value: 'ja', label: '日本語' },
              { value: 'yue', label: '粤语' },
            ]}
          />
          {isRecording && (
            <Tag color="red" className="text-base px-3 py-1">
              {formatTime(duration)}
            </Tag>
          )}
          {!isRecording ? (
            <Button
              type="primary"
              icon={<IoMic />}
              onClick={handleStart}
              size="large"
            >
              开始对比
            </Button>
          ) : (
            <Button
              danger
              icon={<IoStop />}
              onClick={handleStop}
              size="large"
            >
              停止
            </Button>
          )}
        </Space>
      </div>

      <div className="grid grid-cols-2 gap-4" style={{ height: 'calc(100vh - 280px)' }}>
        <TranscriptPanel
          title="Paraformer V2"
          tag="SpeechController"
          tagColor="blue"
          items={speechEngine.items}
          partial={speechEngine.partial}
          connected={speechEngine.connected}
          error={speechEngine.error}
        />
        <TranscriptPanel
          title="Qwen3 ASR Flash"
          tag="QwenAsrController"
          tagColor="purple"
          items={qwenEngine.items}
          partial={qwenEngine.partial}
          connected={qwenEngine.connected}
          error={qwenEngine.error}
        />
      </div>
    </div>
  );
};

export default AsrCompare;
